import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, Typography, FormControl, InputLabel, Select, MenuItem, TextField, Button, Alert, Grid, IconButton, Chip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { apiService } from '../services/api';

const EmailSettings = () => {
  const [selectedBranch, setSelectedBranch] = useState('');
  const [branches, setBranches] = useState([]);
  const [emailSettings, setEmailSettings] = useState({
    service_manager_email: '',
    supply_chain_manager_email: '',
    operations_manager_email: '',
    maintenance_invoice_email: ''
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  // Driver report recipients state
  const [driverReportRecipients, setDriverReportRecipients] = useState([]);
  const [newRecipientEmail, setNewRecipientEmail] = useState('');
  const [driverReportLoading, setDriverReportLoading] = useState(false);
  const [driverReportError, setDriverReportError] = useState(null);
  const [driverReportSuccess, setDriverReportSuccess] = useState('');

  useEffect(() => {
    fetchBranches();
    fetchDriverReportRecipients();
  }, []);

  useEffect(() => {
    if (selectedBranch) {
      fetchBranchSettings();
    }
  }, [selectedBranch]);

  const fetchBranches = async () => {
    try {
      const response = await apiService.getBranches();
      setBranches(response);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch branches');
      setLoading(false);
    }
  };

  const fetchBranchSettings = async () => {
    try {
      setLoading(true);
      const response = await apiService.getBranchByName(selectedBranch);
      setEmailSettings({
        service_manager_email: response.service_manager_email || '',
        supply_chain_manager_email: response.supply_chain_manager_email || '',
        operations_manager_email: response.operations_manager_email || '',
        maintenance_invoice_email: response.maintenance_invoice_email || ''
      });
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch branch settings');
      setLoading(false);
    }
  };

  const handleBranchChange = (event) => {
    setSelectedBranch(event.target.value);
    setError(null);
    setSuccessMessage('');
  };

  const handleEmailChange = (field) => (event) => {
    setEmailSettings(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const validateEmails = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const errors = [];

    Object.entries(emailSettings).forEach(([key, value]) => {
      if (value && !emailRegex.test(value)) {
        errors.push(`Invalid ${key.replace(/_/g, ' ')}`);
      }
    });

    return errors;
  };

  const fetchDriverReportRecipients = async () => {
    try {
      const response = await apiService.getDriverReportRecipients();
      setDriverReportRecipients(response.recipients || []);
    } catch (err) {
      console.error('Failed to fetch driver report recipients:', err);
    }
  };

  const handleAddRecipient = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const email = newRecipientEmail.trim();
    if (!email) return;
    if (!emailRegex.test(email)) {
      setDriverReportError('Please enter a valid email address');
      return;
    }
    if (driverReportRecipients.includes(email)) {
      setDriverReportError('This email is already in the list');
      return;
    }
    setDriverReportRecipients(prev => [...prev, email]);
    setNewRecipientEmail('');
    setDriverReportError(null);
    setDriverReportSuccess('');
  };

  const handleRemoveRecipient = (emailToRemove) => {
    setDriverReportRecipients(prev => prev.filter(e => e !== emailToRemove));
    setDriverReportSuccess('');
  };

  const handleSaveDriverReportRecipients = async () => {
    try {
      setDriverReportLoading(true);
      await apiService.updateDriverReportRecipients(driverReportRecipients);
      setDriverReportSuccess('Driver report recipients updated successfully');
      setDriverReportError(null);
    } catch (err) {
      setDriverReportError('Failed to update driver report recipients');
    } finally {
      setDriverReportLoading(false);
    }
  };

  const handleSubmit = async () => {
    const validationErrors = validateEmails();
    if (validationErrors.length > 0) {
      setError(validationErrors.join(', '));
      return;
    }

    try {
      setLoading(true);
      await apiService.updateEmailSettings(selectedBranch, emailSettings);
      setSuccessMessage('Email settings updated successfully');
      setError(null);
    } catch (err) {
      setError('Failed to update email settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 800, margin: 'auto', mt: 4, p: 2 }}>
      <Typography variant="h4" gutterBottom>Email Settings</Typography>

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Monthly Driver Report Recipients</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            A report listing all drivers and their assigned vehicle unit numbers is emailed on the 1st of each month.
            Add or remove recipients below.
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {driverReportRecipients.map(email => (
              <Chip
                key={email}
                label={email}
                onDelete={() => handleRemoveRecipient(email)}
                deleteIcon={<DeleteIcon />}
              />
            ))}
            {driverReportRecipients.length === 0 && (
              <Typography variant="body2" color="text.secondary">No recipients configured yet.</Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              size="small"
              label="Add email address"
              value={newRecipientEmail}
              onChange={(e) => setNewRecipientEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddRecipient(); } }}
              sx={{ flexGrow: 1 }}
            />
            <IconButton color="primary" onClick={handleAddRecipient} aria-label="Add recipient">
              <AddIcon />
            </IconButton>
          </Box>

          <Button
            variant="contained"
            onClick={handleSaveDriverReportRecipients}
            disabled={driverReportLoading}
          >
            {driverReportLoading ? 'Saving...' : 'Save Recipients'}
          </Button>

          {driverReportError && (
            <Alert severity="error" sx={{ mt: 2 }}>{driverReportError}</Alert>
          )}
          {driverReportSuccess && (
            <Alert severity="success" sx={{ mt: 2 }}>{driverReportSuccess}</Alert>
          )}
        </CardContent>
      </Card>

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <FormControl fullWidth sx={{ mb: 4 }}>
            <InputLabel>Select Branch</InputLabel>
            <Select
              value={selectedBranch}
              label="Select Branch"
              onChange={handleBranchChange}
            >
              {branches.map(branch => (
                <MenuItem key={branch.name} value={branch.name}>
                  {branch.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedBranch && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Service Manager Email"
                  value={emailSettings.service_manager_email}
                  onChange={handleEmailChange('service_manager_email')}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Supply Chain Manager Email"
                  value={emailSettings.supply_chain_manager_email}
                  onChange={handleEmailChange('supply_chain_manager_email')}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Operations Manager Email"
                  value={emailSettings.operations_manager_email}
                  onChange={handleEmailChange('operations_manager_email')}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Maintenance Invoice Email"
                  value={emailSettings.maintenance_invoice_email}
                  onChange={handleEmailChange('maintenance_invoice_email')}
                  helperText="Email address for maintenance invoice notifications from technicians"
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={loading}
                  sx={{ mt: 2 }}
                >
                  {loading ? 'Saving...' : 'Save Settings'}
                </Button>
              </Grid>
            </Grid>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          
          {successMessage && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {successMessage}
            </Alert>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default EmailSettings;